/**
 * Port of `src/components/auth/LoginForm.tsx`.
 *
 * Markup, Zod schema, show/hide-password toggle and the three-phase button
 * label are copied verbatim. Two behavioural edits, both forced by FE-04:
 *
 *   1. `signIn("credentials", …)` -> `useAuth().login(...)`, which exchanges
 *      the credentials for a FastAPI token pair (and, for the length of v2.2,
 *      an Auth.js cookie via the legacy bridge).
 *   2. Success navigates with react-router instead of `window.location.href`.
 *
 * The Google button is rendered disabled rather than deleted: Google sign-in
 * is an Auth.js redirect flow with no FastAPI equivalent yet, and silently
 * dropping a sign-in option users may rely on would be a worse surprise than
 * a visibly unavailable one. `LoginPage` passes `googleEnabled={false}`, so it
 * does not render at all today.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "@/lib/auth/AuthProvider";
import { AuthError } from "@/lib/auth/types";
import { Eye, EyeOff } from "lucide-react";

const loginSchema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z.string().min(1, "Password is required"),
});

type LoginFormData = z.infer<typeof loginSchema>;
type Phase = "idle" | "signing-in" | "redirecting";

interface Props {
  /**
   * Kept in the signature for parity with the Next.js page, but the SPA
   * always renders it false — see `LoginPage`. Google sign-in is an Auth.js
   * redirect flow with no FastAPI token equivalent yet.
   */
  googleEnabled: boolean;
  /**
   * Wave 3 (W12) — a right password on an MFA-enrolled account answers
   * `mfa_required` rather than signing in. `LoginPage` owns what happens
   * next (swapping this form for the code-entry step), so this component's
   * only job is to notice the error code and hand the token up rather than
   * showing it as a generic failure. Optional so this form still degrades
   * sanely (a visible, if unhelpful, error) if ever mounted without a parent
   * that supplies it.
   */
  onMfaRequired?: (mfaToken: string) => void;
}

export function LoginForm({ googleEnabled, onMfaRequired }: Props) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(loginSchema) as any,
  });

  async function onSubmit(data: LoginFormData) {
    setError(null);
    setPhase("signing-in");
    try {
      // PORT NOTE: next-auth's `signIn("credentials")` became the token login.
      // The failure path stays deliberately opaque — the backend answers 401
      // identically for an unknown email, a wrong password and a deactivated
      // account, and the UI must not help anyone tell them apart.
      await login(data.email, data.password);
      setPhase("redirecting");
      // react-router navigation, not `window.location` — the SPA must not do a
      // full document reload here or it would discard the session it just
      // wrote and re-hydrate from storage for nothing.
      navigate("/dashboard", { replace: true });
    } catch (err) {
      if (err instanceof AuthError && err.code === "mfa_required" && err.mfaToken && onMfaRequired) {
        // Not a failure — the password was right. Hand off to the parent's
        // code-entry step and leave this form's error box empty.
        onMfaRequired(err.mfaToken);
        setPhase("idle");
        return;
      }
      if (err instanceof AuthError && err.code === "invalid_credentials") {
        setError("Invalid email or password");
      } else if (err instanceof AuthError) {
        setError(err.message);
      } else {
        setError("Something went wrong. Please try again.");
      }
      setPhase("idle");
    }
  }

  const loading = phase !== "idle";
  const buttonLabel =
    phase === "signing-in"
      ? "Signing in…"
      : phase === "redirecting"
        ? "Redirecting…"
        : "Sign in";

  return (
    <div className="space-y-5">
      {/* Google sign-in */}
      {googleEnabled && (
        <>
          <button
            type="button"
            disabled
            title="Google sign-in is not available on the new frontend yet"
            className="flex w-full items-center justify-center gap-3 rounded-full border border-[var(--cp-line)] bg-[var(--cp-surface)] px-4 py-3 text-sm font-medium text-[var(--cp-ink-2)] shadow-sm transition hover:bg-[var(--cp-surface-2)] disabled:opacity-50"
          >
            <GoogleIcon className="h-5 w-5" />
            Sign in with Google
          </button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[var(--cp-line)]" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-[var(--cp-canvas)] px-3 text-xs font-medium uppercase tracking-wider text-[var(--cp-ink-3)]">
                or
              </span>
            </div>
          </div>
        </>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
            {error}
          </div>
        )}

        <div>
          <label
            htmlFor="email"
            className="mb-1.5 block text-sm font-medium text-[var(--cp-ink-2)]"
          >
            E-mail
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            {...register("email")}
            className="block w-full rounded-xl border border-[var(--cp-line)] bg-[var(--cp-surface)] px-4 py-3 text-sm shadow-sm transition placeholder:text-[var(--cp-ink-3)] focus:border-[var(--cp-accent)] focus:outline-none focus:ring-[3px] focus:ring-[var(--cp-accent-wash)] text-[var(--cp-ink)]"
            placeholder="you@aim.local"
          />
          {errors.email && (
            <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="password"
            className="mb-1.5 block text-sm font-medium text-[var(--cp-ink-2)]"
          >
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              {...register("password")}
              className="block w-full rounded-xl border border-[var(--cp-line)] bg-[var(--cp-surface)] px-4 py-3 pr-11 text-sm shadow-sm transition focus:border-[var(--cp-accent)] focus:outline-none focus:ring-[3px] focus:ring-[var(--cp-accent-wash)] text-[var(--cp-ink)]"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-[var(--cp-ink-3)] hover:text-[var(--cp-ink)]"
              aria-label={showPassword ? "Hide password" : "Show password"}
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-full bg-[var(--cp-accent)] px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-[var(--cp-accent)] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {buttonLabel}
        </button>
      </form>
    </div>
  );
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
      />
    </svg>
  );
}
